/** Repeat editor-owned announcement blocks visually while exposing them once to assistive technology. */
class AnnouncementMarquee extends HTMLElement {
  #resizeObserver;
  #mutationObserver;
  #frame;

  connectedCallback() {
    this.#resizeObserver = new ResizeObserver(() => this.#schedule());
    this.#resizeObserver.observe(this);
    this.#mutationObserver = new MutationObserver(() => this.#schedule());
    const sequence = this.querySelector('.announcement-bar__sequence');
    if (sequence) {
      this.#resizeObserver.observe(sequence);
      this.#mutationObserver.observe(sequence, { childList: true, subtree: true, characterData: true, attributes: true });
    }
    this.#schedule();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame);
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const track = this.querySelector('.announcement-bar__track');
    const sequence = track?.querySelector('.announcement-bar__sequence:not([aria-hidden])');
    if (!track || !sequence) return;

    track.querySelectorAll('.announcement-bar__sequence[aria-hidden]').forEach((copy) => copy.remove());
    this.removeAttribute('data-marquee-ready');
    if (sequence.children.length < 2) return;

    const distance = sequence.getBoundingClientRect().width;
    if (!distance) return;

    // The moving track must always cover the viewport, including just before it loops.
    const repeatCount = Math.ceil(this.clientWidth / distance);
    for (let index = 0; index < repeatCount; index += 1) {
      const duplicate = sequence.cloneNode(true);
      duplicate.setAttribute('aria-hidden', 'true');
      duplicate.setAttribute('inert', '');
      duplicate.querySelectorAll('[id], [ref], [data-shopify-editor-block], [data-shopify-editor-block-type]').forEach((node) => {
        node.removeAttribute('id');
        node.removeAttribute('ref');
        node.removeAttribute('data-shopify-editor-block');
        node.removeAttribute('data-shopify-editor-block-type');
      });
      duplicate.querySelectorAll('a, button, [tabindex]').forEach((node) => node.setAttribute('tabindex', '-1'));
      track.append(duplicate);
    }
    const interval = Number(this.dataset.speed) || 5;
    this.style.setProperty('--marquee-distance', `${distance}px`);
    this.style.setProperty('--marquee-duration', `${distance / (240 / interval)}s`);
    this.setAttribute('data-marquee-ready', '');
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementMarquee);
}
